import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { templates } from './autoplayTemplates';
import { AutoplayOverlay } from '@/store/videoFeatures';
import { AutoplayOverlayPosition, AutoplayOverlaySize } from '@/store/videoFeatures/types/autoplay';
import { Check, Plus, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { UploadControl } from '@/components/UploadControl';
import { useVideoFeaturesStore } from '@/store/videoFeatures/store';

// Posição e tamanho padrão por template ao selecionar pela primeira vez
const TEMPLATE_DEFAULT_LAYOUT: Record<string, { position: AutoplayOverlayPosition; size: AutoplayOverlaySize } | null> = {
  autoPlay:    { position: { xPercent: 50, yPercent: 50 }, size: { widthPercent: 30, heightMode: 'auto' } },
  small:       { position: { xPercent: 18, yPercent: 12 }, size: { widthPercent: 28, heightMode: 'auto' } },
  large:       { position: { xPercent: 50, yPercent: 50 }, size: { widthPercent: 36, heightMode: 'auto' } },
  transparent: { position: { xPercent: 50, yPercent: 50 }, size: { widthPercent: 30, heightMode: 'auto' } },
  custom:      { position: { xPercent: 50, yPercent: 50 }, size: { widthPercent: 30, heightMode: 'auto' } },
  // Demais templates não usam posição livre
  fullScreen:  null,
  blur:        null,
  animation:   null,
};

interface AutoplayTemplateSelectorProps {
  autoplayOverlay: AutoplayOverlay;
  updateAutoplayOverlay: (key: keyof AutoplayOverlay, value: any) => void;
  updateAutoplayOverlayBatch?: (updates: Partial<AutoplayOverlay>) => void;
  hideTitle?: boolean;
}

export const AutoplayTemplateSelector: React.FC<AutoplayTemplateSelectorProps> = ({
  autoplayOverlay,
  updateAutoplayOverlay,
  updateAutoplayOverlayBatch,
  hideTitle = false
}) => {
  useLanguage();
  const setTempFile = useVideoFeaturesStore((state) => state.setTempFile);
  const clearTempFile = useVideoFeaturesStore((state) => state.clearTempFile);
  const blobUrlRef = React.useRef<string | null>(null);

  const revokeBlobIfAny = React.useCallback(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, []);

  React.useEffect(() => () => revokeBlobIfAny(), [revokeBlobIfAny]);
  const handleTemplateSelect = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    if (!template) return;

    const layout = TEMPLATE_DEFAULT_LAYOUT[templateId] ?? null;

    if (updateAutoplayOverlayBatch) {
      updateAutoplayOverlayBatch({
        templateId,
        useTemplate: true,
        customImage: undefined,
        colors: { ...template.colors },
        whiteBorder: template.style?.hasWhiteBorder || false,
        pulseAnimation: template.style?.hasPulse || false,
        // Reseta position/size para o padrão do template selecionado
        position: layout?.position ?? undefined,
        size: layout?.size ?? undefined,
      });
    } else {
      updateAutoplayOverlay('templateId', templateId);
      updateAutoplayOverlay('useTemplate', true);
      updateAutoplayOverlay('customImage', undefined);
      updateAutoplayOverlay('colors', { ...template.colors });
      updateAutoplayOverlay('whiteBorder', template.style?.hasWhiteBorder || false);
      updateAutoplayOverlay('pulseAnimation', template.style?.hasPulse || false);
      updateAutoplayOverlay('position', layout?.position ?? undefined);
      updateAutoplayOverlay('size', layout?.size ?? undefined);
    }
  };

  const handleCustomSelect = () => {
    // Aplica o layout padrão do custom junto com a seleção — sem isso o overlay
    // herdava position/size do template selecionado antes (ex.: o `small`, que
    // fica no topo e estreito), o que não faz sentido para a imagem personalizada.
    const layout = TEMPLATE_DEFAULT_LAYOUT.custom;
    if (updateAutoplayOverlayBatch) {
      updateAutoplayOverlayBatch({
        templateId: 'custom',
        useTemplate: false,
        ...(layout ? { position: layout.position, size: layout.size } : {})
      });
    } else {
      updateAutoplayOverlay('templateId', 'custom');
      updateAutoplayOverlay('useTemplate', false);
      if (layout) {
        updateAutoplayOverlay('position', layout.position);
        updateAutoplayOverlay('size', layout.size);
      }
    }
  };

  const isCustomSelected = autoplayOverlay.templateId === 'custom' || (!!autoplayOverlay.customImage && !autoplayOverlay.useTemplate);

  return (
    <div className="space-y-3">
      {!hideTitle && (
        <h3 className="text-[14px] font-semibold text-[#1A1A1A]">
          {translateInterfaceText("Estilo do Autoplay")}</h3>
      )}
      
      {/* Grid de 7 templates + 1 Personalizado = 8 cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {templates.map((template) => {
          const isSelected = autoplayOverlay.templateId === template.id && autoplayOverlay.useTemplate;
          return (
            <div 
              key={template.id}
              role="button"
              tabIndex={0}
              aria-pressed={isSelected}
              className={cn(
                "relative overflow-hidden cursor-pointer rounded-xl bg-white",
                "transition-all duration-200 ease-out",
                "focus:outline-none focus:ring-1 focus:ring-[#1D4FFF]/30",
                "hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)]",
                isSelected 
                  ? 'border-[2px] border-[#1D4FFF] shadow-sm' 
                  : 'border border-[#E6E6F0] hover:border-[#1D4FFF]/40'
              )}
              onClick={() => handleTemplateSelect(template.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleTemplateSelect(template.id);
                }
              }}
            >
              {isSelected && (
                <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-[#1D4FFF] rounded-full flex items-center justify-center shadow-sm z-10">
                  <Check size={12} className="text-white" strokeWidth={3} />
                </div>
              )}
              
              {/* Preview interno com imagem SVG */}
              <div className="p-2.5">
                <div className="aspect-[4/3] w-full flex items-center justify-center rounded-lg bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 overflow-hidden">
                  <img
                    src={template.image}
                    alt={template.name}
                    className="w-full h-full object-contain"
                  />
                </div>
                {/* Nome do template abaixo */}
                <p className="text-[11px] font-medium text-center text-gray-700 dark:text-gray-300 mt-1.5">
                  {template.name}
                </p>
              </div>
            </div>
          );
        })}

        {/* Card Personalizado - Visual Padronizado */}
        <div 
          role="button"
          tabIndex={0}
          aria-pressed={isCustomSelected}
          className={cn(
            "relative overflow-hidden cursor-pointer rounded-xl bg-white",
            "transition-all duration-200 ease-out",
            "focus:outline-none focus:ring-1 focus:ring-[#1D4FFF]/30",
            "hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)]",
            isCustomSelected 
              ? 'border-[2px] border-[#1D4FFF] shadow-sm' 
              : 'border border-dashed border-[#CBD5E1] hover:border-[#1D4FFF]/40'
          )}
          onClick={handleCustomSelect}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleCustomSelect();
            }
          }}
        >
          {isCustomSelected && (
            <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-[#1D4FFF] rounded-full flex items-center justify-center shadow-sm z-10">
              <Check size={12} className="text-white" strokeWidth={3} />
            </div>
          )}
          
          {/* Preview interno */}
          <div className="p-2.5">
            <div className="aspect-[4/3] w-full flex flex-col items-center justify-center gap-1.5 rounded-lg bg-[#F8FAFC]">
              {autoplayOverlay.customImage ? (
                <img
                  src={autoplayOverlay.customImage}
                  alt={translateInterfaceText("Custom")}
                  className="w-full h-full object-contain rounded-lg"
                />
              ) : (
                <>
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#1D4FFF] to-[#60A5FA] flex items-center justify-center">
                    <Plus className="w-4 h-4 text-white" />
                  </div>
                </>
              )}
            </div>
            <p className="text-[11px] font-medium text-center text-gray-700 dark:text-gray-300 mt-1.5">
              {translateInterfaceText("Personalizado")}</p>
          </div>
        </div>
      </div>

      {/* Bloco Inline de Upload - Aparece apenas quando Personalizado está selecionado */}
      {isCustomSelected && (
        <div className="mt-4 animate-in slide-in-from-top-2 duration-200">
          <UploadControl
            label={translateInterfaceText("Imagem do Autoplay")}
            accept="image/*"
            maxSize={20}
            preview={autoplayOverlay.customImage || null}
            onFileSelected={(file: File) => {
              revokeBlobIfAny();
              const blobUrl = URL.createObjectURL(file);
              blobUrlRef.current = blobUrl;
              setTempFile('autoplayCustomImageFile', file);
              if (updateAutoplayOverlayBatch) {
                updateAutoplayOverlayBatch({
                  customImage: blobUrl,
                  useTemplate: false,
                  templateId: 'custom'
                });
              } else {
                updateAutoplayOverlay('customImage', blobUrl);
              }
            }}
            onClear={() => {
              revokeBlobIfAny();
              clearTempFile('autoplayCustomImageFile');
              if (updateAutoplayOverlayBatch) {
                updateAutoplayOverlayBatch({
                  customImage: undefined,
                  useTemplate: true,
                  templateId: 'clean'
                });
              } else {
                updateAutoplayOverlay('customImage', undefined);
              }
            }}
            isActive={!!autoplayOverlay.customImage}
          />
          
          {/* Dica de proporção */}
          <div className="flex items-center gap-2 px-3 py-2 bg-[#EBF5FF] rounded-lg mt-3">
            <Info className="w-3.5 h-3.5 text-[#1D4FFF] flex-shrink-0" />
            <p className="text-[11px] text-[#1D4FFF]">
              {translateInterfaceText("Use imagens com proporção 16:9 para melhor resultado")}</p>
          </div>
        </div>
      )}
    </div>
  );
};
